"use client";

import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";

interface MarkdownComponentsProps {
  markdownContent: string;
}

/**
 * Markdown renderer component with custom styling
 */
export function MarkdownComponents({ markdownContent }: MarkdownComponentsProps) {
  const markdownComponent = useMemo(() => {
    if (!markdownContent) return null;

    return (
      <div className="prose max-w-none dark:prose-invert prose-p:my-0">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
          components={{
            // Comfortable on phones (text-sm); md+ targets ~16px body for dense AI prose on desktop.
            h1: ({ children }) => (
              <h1 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-base md:text-lg font-semibold mb-2 md:mb-3 mt-1 md:mt-2">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-sm md:text-base font-semibold mb-1.5 md:mb-2 mt-1">{children}</h3>
            ),
            p: ({ children }) => (
              <p className="mb-2 md:mb-3 text-sm md:text-base leading-relaxed md:leading-[1.6] text-balance">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-inside mb-2 md:mb-3 space-y-1 md:space-y-1.5">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside mb-2 md:mb-3 space-y-1 md:space-y-1.5">{children}</ol>
            ),
            li: ({ children }) => (
              <li className="text-sm md:text-base leading-relaxed md:leading-snug">{children}</li>
            ),
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            code: ({ children }) => (
              <code className="bg-muted/80 px-1 py-0.5 rounded text-xs md:text-[0.8125rem] font-mono">
                {children}
              </code>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-border pl-3 md:pl-4 my-3 md:my-4 italic text-sm md:text-base text-muted-foreground">
                {children}
              </blockquote>
            ),
          }}
        >
          {markdownContent}
        </ReactMarkdown>
      </div>
    );
  }, [markdownContent]);

  return markdownComponent;
}
